import omit from 'lodash-es/omit';
import { createCachedSelector } from 're-reselect';
import {
  AddressBookInstances,
  AddressHash,
  APIAddressHash,
} from '~/reducers/address/address.reducer';
import { createSelector } from 'reselect';
import { areAddressesFullyEqual } from '~/types/Address';
import APIAddress, { SavedAPIAddress } from '~/types/APIAddress';
import { ApplicationState } from '../store';

export const getAddressStore = (state: ApplicationState) => state.address;

export const getAddressKeyProp = (_: unknown, props: { addressKey: string }): string =>
  props.addressKey;
export const getAddressBookNameProp = (_: unknown, props: { addressBookName: string }) =>
  props.addressBookName;

export const getAddresses = createSelector(
  getAddressStore,
  (addressStore): APIAddressHash => addressStore.apiAddresses,
);

export const getAddressBookInstances = createSelector(
  getAddressStore,
  (addressStore): AddressBookInstances => addressStore.addressBookInstances,
);

export const getIsAddressBookInitialized = createCachedSelector(
  getAddressBookInstances,
  getAddressBookNameProp,
  (addressBookInstances, addressBookName): boolean =>
    addressBookInstances[addressBookName] && addressBookInstances[addressBookName].initialized,
)(getAddressBookNameProp);

export const getSelectedAddresses = createCachedSelector(
  getAddressStore,
  getAddressBookNameProp,
  (addressStore, addressBookName): AddressHash => {
    return addressStore.addressBookInstances[addressBookName]
      ? addressStore.addressBookInstances[addressBookName].selectedAddresses
      : ({} as AddressHash);
  },
)(getAddressBookNameProp);

export const getSelectedAddress = createCachedSelector(
  getAddresses,
  getSelectedAddresses,
  getAddressKeyProp,
  (addresses, selectedAddresses, addressKey): Maybe<APIAddress> => {
    const selectedAddress = selectedAddresses[addressKey];
    // TODO: Change return to Maybe<APIAddress | BaseAddress>
    switch (typeof selectedAddress) {
      case 'number':
        return addresses.hasOwnProperty(selectedAddress) ? addresses[selectedAddress] : null;
      default:
        return selectedAddress as APIAddress;
    }
  },
)(getAddressKeyProp);

export const getSelectedAddressesData = createSelector(
  getAddresses,
  getSelectedAddresses,
  (addresses, selectedAddresses) =>
    Object.keys(selectedAddresses).reduce<AddressHash>((obj, key) => {
      const selectedAddress = selectedAddresses[key];
      switch (typeof selectedAddress) {
        case 'number':
          return { ...obj, [key]: addresses[selectedAddress] };
        default:
          return { ...obj, [key]: selectedAddress };
      }
    }, {}),
);

export const getIsSelectedAddressInSavedAddresses = createSelector(
  getAddresses,
  getSelectedAddress,
  (savedAddresses, selectedAddress): boolean => {
    if (!selectedAddress) {
      return false;
    }

    return !!Object.values(savedAddresses).find((address) =>
      areAddressesFullyEqual(address, selectedAddress),
    );
  },
);

export const getAddressesWithSelectedFirst = createSelector(
  getAddresses,
  getSelectedAddress,
  (addresses, selectedAddress): readonly APIAddress[] =>
    selectedAddress
      ? [
          selectedAddress,
          ...Object.values(
            selectedAddress.id
              ? (omit(addresses, [selectedAddress.id]) as APIAddressHash)
              : addresses,
          ),
        ]
      : Object.values(addresses),
);

export const getIsUpdatingApiAddress = createSelector(
  getAddressStore,
  (addressStore): boolean => addressStore.isUpdatingApiAddress,
);

export const getIsCreatingApiAddress = createSelector(
  getAddressStore,
  (addressStore): boolean => addressStore.isCreatingApiAddress,
);

export const getIsDeletingApiAddress = createSelector(
  getAddressStore,
  (addressStore): boolean => addressStore.isDeletingApiAddress,
);

export const getAddressErrorMessage = createSelector(
  getAddressStore,
  (addressStore): string => addressStore.errorMessage,
);

export const getIsLoading = createSelector(
  getAddressStore,
  (addressStore): boolean => addressStore.isLoadingApiAddresses,
);

export const getDefaultSenderAddress = createSelector(
  getAddressStore,
  (addressStore): SavedAPIAddress | null => {
    const defaultAddresses = Object.keys(addressStore.apiAddresses)
      .map((key) => addressStore.apiAddresses[key])
      .filter((address) => address.isDefaultSender);
    return defaultAddresses.length ? defaultAddresses[0] : null;
  },
);
